import { SubscriptionPeriod, SubscriptionPlan, SubscriptionStatusDTO } from 'models/user/userWorkspaceDTOs'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { useQuery } from '@tanstack/react-query'
import { RoleMimetype } from 'constants/API'
import { Path } from 'constants/router/paths'
import { useAPI } from 'utils/API'

enum Endpoints {
  GET_SUBSCRIPTION_CHECKOUT_LINK = '/workspace/{workspaceId}/subscribe',
  CONFIRM_SUBSCRIPTION = '/workspace/{workspaceId}/subscribe/confirm',
  GET_SUBSCRIPTION_PORTAL_LINK = '/workspace/{workspaceId}/subscription/manage',
}

interface SubscriptionCheckoutDetails {
  plan: SubscriptionPlan
  period: SubscriptionPeriod
}

interface GetSubscriptionCheckoutLinkPayload {
  workspaceId: string
  period: SubscriptionPeriod
  plan: SubscriptionPlan
}

// QUERIES

export function useGetPortalLink(workspaceId: string, returnUrl: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    // Keep workspaceId in the key — the link grants access to that workspace's billing
    queryKey: [EntityKeys.PORTAL_LINK, QueryType.GET, { workspaceId }],
    queryFn: () => api.post<{ url: string }>(
      Endpoints.GET_SUBSCRIPTION_PORTAL_LINK,
      { workspaceId: workspaceId?.toString() || '' },
      {
        // Ensure escape for safety
        returnUrl: encodeURI(returnUrl)
      },
      false
    ),
    // Makes query not run automatically
    enabled: false,
    // One click, one request — the default retry: 3 mints 4 portal sessions over ~7s
    retry: false
  })
}

// MUTATIONS

/**
 * Gets the subscription checkout link for a workspace (as a mutation)
 * Use this for triggering checkout link generation in flows like subscription creation
 */
export function useGetSubscriptionCheckoutLink() {
  const api = useAPI<Endpoints>()

  return getMutation<{ url: string }, GetSubscriptionCheckoutLinkPayload>(
    ({ workspaceId, period, plan }) => api.post(
      Endpoints.GET_SUBSCRIPTION_CHECKOUT_LINK,
      { workspaceId },
      {
        plan,
        period,
        returnUrl: encodeURI(`${window.location.origin}${Path.SUBSCRIPTION_STATUS}`)
      },
      false,
      {
        headers: {
          Accept: RoleMimetype.CLIENT
        }
      }
    )
  )
}

/**
 * Confirms a successful Chargebee checkout for the given workspace.
 * Called after Chargebee redirects the user back to the configured returnUrl.
 * Activates the grace period + paid plan using the `pendingCheckoutPlan` recorded
 * server-side by the prior `/subscribe` call. Best-effort and idempotent — the
 * Chargebee webhook is the authoritative source of truth and will reconcile.
 */
export function useConfirmSubscription() {
  const api = useAPI<Endpoints>()

  return getMutation<SubscriptionStatusDTO, { workspaceId: string }>(
    ({ workspaceId }) => api.post(
      Endpoints.CONFIRM_SUBSCRIPTION,
      { workspaceId },
      {},
      false,
      {
        headers: {
          Accept: RoleMimetype.CLIENT
        }
      }
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}
